import { useState } from "react";
import { Check, ChevronDown, ArrowRight, Ban } from "lucide-react";
import {
  PREMIUM_TIERS,
  TIER_DELIVERABLES,
  TIER_EXCLUSIONS,
  type PremiumTier,
} from "@/lib/fsap-data";
import { StrategySessionModal } from "@/components/advocacy/StrategySessionModal";
import { trackCTA } from "@/lib/tracking";
import { setSelectedBookTier } from "@/lib/book-click-tracking";

/**
 * Tier-specific deliverables checklist. Selecting a retainer level expands the
 * exact onboarding / monthly / quarterly deliverables for that tier.
 */
export const TierDeliverables = () => {
  const [selected, setSelected] = useState<PremiumTier["id"] | null>(null);
  const [modalTier, setModalTier] = useState<PremiumTier | null>(null);

  const handleSelect = (id: PremiumTier["id"]) => {
    const next = selected === id ? null : id;
    setSelected(next);
    setSelectedBookTier(next, "tier_deliverables");
    if (next) trackCTA("fsap_deliverables_expand", { tier: next });
  };

  const tier = PREMIUM_TIERS.find((t) => t.id === selected);
  const detail = selected ? TIER_DELIVERABLES[selected] : null;

  return (
    <div>
      <h2 className="font-display text-3xl md:text-4xl">What You Actually Receive</h2>
      <p className="text-fg-secondary mt-3 max-w-2xl">
        Select a retainer level to expand the full deliverables checklist — onboarding,
        monthly, and quarterly — plus what is explicitly out of scope.
      </p>

      <div
        role="tablist"
        aria-label="Retainer levels"
        className="mt-8 grid sm:grid-cols-3 gap-3"
      >
        {PREMIUM_TIERS.map((t) => {
          const active = selected === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-expanded={active}
              aria-controls="tier-deliverables-panel"
              onClick={() => handleSelect(t.id)}
              className={`text-left border p-5 transition-colors ${
                active
                  ? "border-gold bg-gold/10"
                  : "border-fg-muted/15 hover:border-gold/40 hover:bg-bg-secondary/40"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display uppercase tracking-[0.18em] text-gold text-xs">
                    {t.name}
                  </div>
                  <div className="mt-2 font-display text-2xl">
                    ${t.monthly.toLocaleString()}
                    <span className="text-fg-secondary text-xs font-sans"> /month</span>
                  </div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-fg-muted shrink-0 mt-1 transition-transform ${
                    active ? "rotate-180 text-gold" : ""
                  }`}
                />
              </div>
              <p className="mt-3 text-xs text-fg-secondary">{t.tagline}</p>
            </button>
          );
        })}
      </div>

      <div
        id="tier-deliverables-panel"
        role="region"
        aria-live="polite"
        aria-label="Tier deliverables"
      >
        {tier && detail ? (
          <div className="mt-6 border border-gold/30 bg-bg-secondary/40 p-6 md:p-8">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <h3 className="font-display text-2xl">{tier.name} deliverables</h3>
              {detail.inheritsFrom && (
                <span className="text-xs uppercase tracking-[0.14em] text-gold">
                  Includes everything in {detail.inheritsFrom}
                </span>
              )}
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-8">
              {detail.groups.map((g) => (
                <div key={g.group}>
                  <div className="text-xs uppercase tracking-[0.16em] text-fg-muted">
                    {g.group}
                  </div>
                  <ul className="mt-3 space-y-2.5">
                    {g.items.map((item) => (
                      <li key={item} className="flex gap-2 text-sm">
                        <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-fg-muted/15">
              <div className="text-xs uppercase tracking-[0.16em] text-fg-muted">
                Not included at any tier
              </div>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {TIER_EXCLUSIONS.map((x) => (
                  <li key={x} className="flex gap-2 text-sm text-fg-secondary">
                    <Ban className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="button"
              onClick={() => {
                trackCTA("fsap_deliverables_apply", { tier: tier.id, monthly: tier.monthly });
                setModalTier(tier);
              }}
              className="mt-8 inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:brightness-110"
            >
              Apply for {tier.name.split(" ")[0]}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-fg-muted">
            Choose a tier above to see the full checklist.
          </p>
        )}
      </div>

      <StrategySessionModal
        open={!!modalTier}
        onOpenChange={(o) => !o && setModalTier(null)}
        tierLabel={modalTier?.name ?? ""}
        tierId={modalTier?.id}
        source="advocacy_pricing_deliverables"
      />
    </div>
  );
};
